import { TextAlignment, TextBoxHeightState, VideoData } from '@lumen5/luminary';

import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import { setAssetPositionTransformationOperation } from 'js/src/luminary/luminary-operations/core';
import {
    getMinimumResizeHeight,
    getOneLineTextFloor,
    getResizeBaselineHeight,
    getResizeFloor,
    snapshotTextBox,
} from 'js/src/luminary/luminary-selectors';
import type {
    TextBoxBeforeWidthChange,
    TextBoxHeightBeforeDrag,
} from 'js/src/luminary/luminary-selectors';
import { updatePositionAndAnchor } from 'js/src/luminary/text-editing-operations';

/** Narrowing frees a box that still clips its text; widening freezes the height it had. */
function settleTextBoxHeight(before: TextBoxBeforeWidthChange | null, narrowed: boolean) {
    if (!before) {
        return;
    }
    const { box, previousUnclippedHeight, previousEffectiveHeight } = before;
    if (narrowed) {
        if (box.unclippedHeight <= previousUnclippedHeight + VideoData.LINE_HEIGHT_RENDER_SLACK) {
            return;
        }
        if (box.heightState === TextBoxHeightState.Truncated) {
            box.hasUserChangedMaxHeight = false;
        }
        return;
    }
    if (box.unclippedHeight >= previousUnclippedHeight - VideoData.LINE_HEIGHT_RENDER_SLACK) {
        return;
    }
    box.hasUserChangedMaxHeight = true;
    box.maxHeight = previousEffectiveHeight;
}

export const resizeLeftAssetOperation = createLuminaryOperation(
    (
        { luminary, call },
        {
            sceneId,
            assetId,
            width,
            heightBeforeDrag,
        }: {
            sceneId: string;
            assetId: string;
            width: number;
            heightBeforeDrag?: TextBoxHeightBeforeDrag;
        },
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            throw new Error('resizeLeftAssetOperation: scene not found');
        }
        const asset = scene.getAssetById(assetId);
        if (!asset) {
            throw new Error('resizeLeftAssetOperation: asset not found');
        }

        if (VideoData.isTextAsset(asset) || VideoData.isFancyCaptionAsset(asset)) {
            if (asset.maxWidth === width) {
                return;
            }

            const diff = asset.maxWidth - width;
            const boxBefore = snapshotTextBox(asset, heightBeforeDrag);
            luminary.batchRun(() => {
                asset.maxWidth = width;
                settleTextBoxHeight(boxBefore, diff > 0);

                if (asset.textAlign === TextAlignment.LEFT) {
                    asset.transformation.position.x += diff;
                } else if (asset.textAlign === TextAlignment.CENTER) {
                    asset.transformation.position.x += diff / 2;
                }
            });
        } else if (VideoData.isAssetGroup(asset)) {
            if (asset.approximateMaxBounds.width === width) {
                return;
            }

            const diff = asset.approximateMaxBounds.width - width;

            const boxBefore = snapshotTextBox(asset, heightBeforeDrag);
            const originalMaxWidths = asset
                .getAssetsByType(VideoData.TEXT_ASSET_TYPE)
                .map((childAsset) => childAsset.maxWidth);

            // Outside the batch below: the revert check needs the width these writes measure to.
            asset.getAssetsByType(VideoData.TEXT_ASSET_TYPE).forEach((childAsset) => {
                childAsset.maxWidth = width;
            });

            if (asset.approximateMaxBounds.width > width) {
                asset.getAssetsByType(VideoData.TEXT_ASSET_TYPE).forEach((childAsset, i) => {
                    childAsset.maxWidth = originalMaxWidths[i];
                });

                return;
            }

            luminary.batchRun(() => {
                settleTextBoxHeight(boxBefore, diff > 0);

                if (asset.alignChildren === TextAlignment.LEFT) {
                    call(setAssetPositionTransformationOperation, {
                        sceneId,
                        assetId: asset.id,
                        x: asset.transformation.position.x + diff,
                        y: asset.transformation.position.y,
                    });
                } else if (asset.alignChildren === TextAlignment.CENTER) {
                    call(setAssetPositionTransformationOperation, {
                        sceneId,
                        assetId: asset.id,
                        x: asset.transformation.position.x + diff / 2,
                        y: asset.transformation.position.y,
                    });
                }
            });
        }
    },
);

export const resizeRightAssetOperation = createLuminaryOperation(
    (
        { luminary, call },
        {
            sceneId,
            assetId,
            width,
            heightBeforeDrag,
        }: {
            sceneId: string;
            assetId: string;
            width: number;
            heightBeforeDrag?: TextBoxHeightBeforeDrag;
        },
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            throw new Error('resizeRightAssetOperation: scene not found');
        }
        const asset = scene.getAssetById(assetId);
        if (!asset) {
            throw new Error('resizeRightAssetOperation: asset not found');
        }

        if (VideoData.isTextAsset(asset) || VideoData.isFancyCaptionAsset(asset)) {
            if (asset.maxWidth === width) {
                return;
            }

            const diff = asset.maxWidth - width;
            const boxBefore = snapshotTextBox(asset, heightBeforeDrag);
            luminary.batchRun(() => {
                asset.maxWidth = width;
                settleTextBoxHeight(boxBefore, diff > 0);

                if (asset.textAlign === TextAlignment.RIGHT) {
                    asset.transformation.position.x -= diff;
                } else if (asset.textAlign === TextAlignment.CENTER) {
                    asset.transformation.position.x -= diff / 2;
                }

                if (asset.transformation.position.y < 0) {
                    asset.transformation.position.y = 0;
                }
            });
        }
        if (VideoData.isAssetGroup(asset)) {
            if (asset.approximateMaxBounds.width === width) {
                return;
            }

            const diff = asset.approximateMaxBounds.width - width;

            const boxBefore = snapshotTextBox(asset, heightBeforeDrag);
            const originalMaxWidths = asset
                .getAssetsByType(VideoData.TEXT_ASSET_TYPE)
                .map((childAsset) => childAsset.maxWidth);

            // Outside the batch below: the revert check needs the width these writes measure to.
            asset.getAssetsByType(VideoData.TEXT_ASSET_TYPE).forEach((childAsset) => {
                childAsset.maxWidth = width;
            });

            if (asset.approximateMaxBounds.width > width) {
                asset.getAssetsByType(VideoData.TEXT_ASSET_TYPE).forEach((childAsset, i) => {
                    childAsset.maxWidth = originalMaxWidths[i];
                });

                return;
            }

            luminary.batchRun(() => {
                settleTextBoxHeight(boxBefore, diff > 0);

                if (asset.alignChildren === TextAlignment.RIGHT) {
                    call(setAssetPositionTransformationOperation, {
                        sceneId,
                        assetId: asset.id,
                        x: asset.transformation.position.x - diff,
                        y: asset.transformation.position.y,
                    });
                } else if (asset.alignChildren === TextAlignment.CENTER) {
                    call(setAssetPositionTransformationOperation, {
                        sceneId,
                        assetId: asset.id,
                        x: asset.transformation.position.x - diff / 2,
                        y: asset.transformation.position.y,
                    });
                }
            });
        }
    },
);

export const resizeAssetInitOperation = createLuminaryOperation(
    ({ luminary }, { sceneId, assetId }: { sceneId: string; assetId: string }) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            throw new Error('resizeAssetInitOperation: scene not found');
        }
        const asset = scene.getAssetById(assetId);
        if (!asset) {
            throw new Error('resizeAssetInitOperation: asset not found');
        }

        if (VideoData.isTextAsset(asset)) {
            luminary.batchRun(() => {
                asset.maxWidth = asset.getNTEContainerBounds().width;
                asset.hasUserChangedMaxWidth = true;
                updatePositionAndAnchor(asset, asset.boundsWithoutAnimation.width, asset.textAlign);
            });
        } else if (VideoData.isFancyCaptionAsset(asset)) {
            luminary.batchRun(() => {
                asset.maxWidth = asset.width;
                asset.hasUserChangedMaxWidth = true;
                updatePositionAndAnchor(asset, asset.boundsWithoutAnimation.width, asset.textAlign);
            });
        } else if (VideoData.isAssetGroup(asset)) {
            asset.hasUserChangedMaxWidth = true;

            const maxChildRealWidth = asset
                .getAssetsByType(VideoData.TEXT_ASSET_TYPE)
                .reduce((maxWidth, childAsset) => {
                    return childAsset.hasText
                        ? Math.max(maxWidth, childAsset.getNTEContainerBounds().width)
                        : maxWidth;
                }, 0);

            updatePositionAndAnchor(asset, asset.boundsWithoutAnimation.width, asset.alignChildren);

            luminary.batchRun(() => {
                asset.getAssetsByType(VideoData.TEXT_ASSET_TYPE).forEach((childAsset) => {
                    childAsset.maxWidth = maxChildRealWidth;
                });
            });
        }
    },
);

export const resizeTopAssetOperation = createLuminaryOperation(
    (
        { luminary },
        { sceneId, assetId, height }: { sceneId: string; assetId: string; height: number },
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            throw new Error('resizeTopAssetOperation: scene not found');
        }
        const asset = scene.getAssetById(assetId);
        if (!asset) {
            throw new Error('resizeTopAssetOperation: asset not found');
        }

        if (
            !VideoData.isTextAsset(asset) &&
            !VideoData.isFancyCaptionAsset(asset) &&
            !VideoData.isAssetGroup(asset)
        ) {
            return;
        }

        const previousEffectiveHeight = getResizeBaselineHeight(asset);
        const resizeFloor = getResizeFloor(asset, previousEffectiveHeight);
        const requestedHeight = Math.max(height, resizeFloor);
        if (previousEffectiveHeight === requestedHeight) {
            return;
        }

        const diff = previousEffectiveHeight - requestedHeight;
        const anchorY = asset.transformation.anchor.y;
        const previousY = asset.transformation.position.y;
        const previousTopEdge = previousY - anchorY * previousEffectiveHeight;
        const proposedTopEdge = previousTopEdge + diff;

        luminary.batchRun(() => {
            asset.hasUserChangedMaxHeight = true;
            if (proposedTopEdge < 0) {
                const heightToBottomEdge = previousEffectiveHeight + previousTopEdge;
                const clampedHeight = Math.max(heightToBottomEdge, resizeFloor);
                asset.maxHeight = clampedHeight;
                asset.transformation.position.y = anchorY * clampedHeight;
            } else {
                asset.maxHeight = requestedHeight;
                asset.transformation.position.y = previousY + (1 - anchorY) * diff;
            }
        });
    },
);

export const resizeBottomAssetOperation = createLuminaryOperation(
    (
        { luminary },
        { sceneId, assetId, height }: { sceneId: string; assetId: string; height: number },
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            throw new Error('resizeBottomAssetOperation: scene not found');
        }
        const asset = scene.getAssetById(assetId);
        if (!asset) {
            throw new Error('resizeBottomAssetOperation: asset not found');
        }

        if (
            !VideoData.isTextAsset(asset) &&
            !VideoData.isFancyCaptionAsset(asset) &&
            !VideoData.isAssetGroup(asset)
        ) {
            return;
        }

        const previousEffectiveHeight = getResizeBaselineHeight(asset);
        const resizeFloor = getResizeFloor(asset, previousEffectiveHeight);
        const requestedHeight = Math.max(height, resizeFloor);
        if (previousEffectiveHeight === requestedHeight) {
            return;
        }

        const anchorY = asset.transformation.anchor.y;
        luminary.batchRun(() => {
            asset.hasUserChangedMaxHeight = true;
            asset.maxHeight = requestedHeight;
            asset.transformation.position.y +=
                anchorY * (requestedHeight - previousEffectiveHeight);
        });
    },
);

/** Grows an AssetGroup's maxHeight so an added text child fits. */
export const resetAssetGroupAutoFitForAddedTextOperation = createLuminaryOperation(
    ({ luminary }, { sceneId, assetId }: { sceneId: string; assetId: string }) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) return;
        const target = scene.getAssetById(assetId);
        if (!target) return;
        const group = target.getParentByType(VideoData.GROUP_ASSET_TYPE);
        if (!group) return;

        const targetIsEmptyTextPlaceholder =
            (VideoData.isTextAsset(target) || VideoData.isFancyCaptionAsset(target)) &&
            !target.hasText;
        const requiredHeight = (() => {
            if (!targetIsEmptyTextPlaceholder) return getMinimumResizeHeight(group);
            // minimumStackedHeight excludes the empty target; reserve its own floor, plus a gap
            // only when the group already has visible children.
            const targetFloor = getOneLineTextFloor(target);
            const visibleChildrenFloor = group.minimumStackedHeight;
            if (visibleChildrenFloor <= 0) return targetFloor;
            return Math.ceil(visibleChildrenFloor) + targetFloor + group.spaceBetweenChildren;
        })();
        if (group.maxHeight >= requiredHeight) return;
        group.maxHeight = requiredHeight;
    },
);
